import {
  FilterAction,
  FilterKeys,
  PaymentFilter,
  PriceFilter,
  VehicleFilter,
  VehicleFilterPricing,
} from "@modules/vehicle/types/vehicleFilter";
import FilterDisclosure from "@modules/vehicle/components/FilterDisclosure";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { SlideToggle } from "@talladega/components";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";
import VehiclePaymentFilter from "./VehiclePaymentFilter";
import VehiclePriceFilter from "./VehiclePriceFilter";
import { PaymentFactors } from "../hooks/useVehicleList";

const isPaymentFilterActive = (paymentFilter: PaymentFilter | undefined) =>
  paymentFilter !== undefined &&
  paymentFilter.state.maxPayment != null &&
  paymentFilter.state.minPayment != null;

const isPriceFilterActive = (priceFilter: PriceFilter | undefined) =>
  priceFilter !== undefined;

type Props = {
  onUpdate(filterAction: FilterAction): void;
  vehicleFilters: VehicleFilter[];
  pricing: VehicleFilterPricing;
  paymentFactors?: PaymentFactors;
  defaultOpen?: boolean;
};

const VehiclePricingFilter = ({
  onUpdate,
  vehicleFilters,
  pricing,
  paymentFactors,
  defaultOpen = false,
}: Props) => {
  const { t } = useTranslation(["vehiclesFilters"]);

  const paymentFilter = vehicleFilters.find(
    (vf): vf is PaymentFilter => vf.key === FilterKeys.Payment
  );
  const priceFilter = vehicleFilters.find(
    (vf): vf is PriceFilter => vf.key === FilterKeys.Price
  );

  const [pricingView, setPricingView] = useState<
    "price" | "payment" | undefined
  >(priceFilter !== undefined ? "price" : "payment");

  useEffect(() => {
    if (isPriceFilterActive(priceFilter)) {
      setPricingView("price");
    } else if (isPaymentFilterActive(paymentFilter)) {
      setPricingView("payment");
    }
  }, [priceFilter, paymentFilter]);

  return (
    <FilterDisclosure
      active={
        isPaymentFilterActive(paymentFilter) || isPriceFilterActive(priceFilter)
      }
      headerText={t("vehiclesFilters:pricing_payments")}
      defaultOpen={defaultOpen}
      childPadding="xs"
      onOpen={() => hotjar.event(HotjarEvent.VlpFilterPricingPayment)}
    >
      <>
        <SlideToggle
          leftLabel={t("vehiclesFilters:financing")}
          leftValue="payment"
          rightLabel={t("vehiclesFilters:price")}
          rightValue="price"
          value={pricingView}
          onChange={setPricingView}
        />

        {pricingView === "payment" && (
          <VehiclePaymentFilter
            onUpdate={onUpdate}
            paymentFilter={paymentFilter}
            pricing={pricing}
            paymentFactors={paymentFactors}
          />
        )}
        {pricingView === "price" && (
          <VehiclePriceFilter
            onUpdate={onUpdate}
            priceFilter={priceFilter}
            pricing={pricing}
          />
        )}
      </>
    </FilterDisclosure>
  );
};
export default VehiclePricingFilter;
